import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, SlicePipe } from '@angular/common';
import { MenuItem, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { StyleClassModule } from 'primeng/styleclass';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { AppConfigurator } from './app.configurator';
import { AppNotificaciones } from './app.notificaciones';
import { LayoutService } from '../service/layout.service';
import { AuthService } from '@/pages/service/auth.service';
import { UsersService } from '@/modules/users/services/users/users.service';
import { NotificacionesInvitacionService } from '@/shared/services/notificaciones-invitacion.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-topbar',
    standalone: true,
    templateUrl: './app.topbar.html',
    styleUrls: ['./app.topbar.css'],
    imports: [
        CommonModule,
        StyleClassModule,
        AppConfigurator,
        AppNotificaciones,
        ButtonModule,
        DialogModule,
        ProgressSpinnerModule,
        ToastModule,
        TooltipModule,
        SlicePipe
    ],
    providers: [MessageService]
})
export class AppTopbar {
    items!: MenuItem[];

    /** El panel de notificaciones solo aplica al administrador principal (sin sede asignada). */
    readonly mostrarNotificaciones: boolean;

    userAuth: any | null = null;
    photoUrl: string | null = null;
    isUploading = false;
    showPhotoModal = false;

    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

    constructor(
        public layoutService: LayoutService,
        private readonly authService: AuthService,
        private readonly usersService: UsersService,
        private readonly messageService: MessageService,
        private readonly router: Router,
        private readonly notificacionesService: NotificacionesInvitacionService
    ) {
        this.userAuth = this.authService.user ? JSON.parse(this.authService.user) : null;
        this.photoUrl = this.normalizarUrlFoto(this.userAuth?.foto_perfil_url);

        const mainRole = (this.userAuth?.roles?.[0] ?? '').toLowerCase();
        this.mostrarNotificaciones =
            !!this.userAuth &&
            mainRole !== 'superadministrador' &&
            (this.userAuth.sede_id === null || this.userAuth.sede_id === undefined);
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }

    openPhotoModal(): void {
        if (this.photoUrl) {
            this.showPhotoModal = true;
        }
    }

    triggerFileInput(): void {
        this.fileInputRef.nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        this.isUploading = true;

        this.usersService.uploadProfilePhoto(this.userAuth.id, file).subscribe({
            next: (response: any) => {
                const url = this.normalizarUrlFoto(response?.data?.foto_perfil_url) ?? '';
                this.photoUrl = url;
                this.userAuth = { ...this.userAuth, foto_perfil_url: url };
                this.authService.user = JSON.stringify(this.userAuth);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Foto actualizada',
                    detail: 'Tu foto de perfil se actualizó correctamente',
                    life: 3000
                });
                this.showPhotoModal = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudo actualizar la foto de perfil',
                    life: 5000
                });
            },
            complete: () => {
                this.isUploading = false;
                input.value = '';
            }
        });
    }

    logout(): void {
        this.notificacionesService.detener();
        this.authService.logout().subscribe({
            next: () => {
                this.router.navigate(['/auth/login']);
            },
            error: (error) => {
                console.error('Error al cerrar sesión', error);
            }
        });
    }

    /** El backend a veces devuelve la foto con un host distinto al de environment.apiBack; se normaliza al mismo origen. */
    private normalizarUrlFoto(url: string | null | undefined): string | null {
        if (!url) return null;
        const backendOrigin = new URL(environment.apiBack).origin;
        return url.replace(/^https?:\/\/[^/]+/, backendOrigin);
    }
}
